import { getToken } from "firebase/messaging";
import { messaging } from "../../firebase";
import { authenticatedFetch } from "./apiHelpers";
import { REGISTER_DEVICE_TOKEN_URL } from "../../config/api";

const VAPID_KEY =
  "BIFv5ltdZt0CBKW3qlO2y9e9lywqgr0GzsB2e4j-jI8hrGQX3rsU6PIr8IzZRQ39hdBTAbBwjYkr6Qux4ojN6kE";

const FCM_REGISTERED_TOKEN_KEY = "fcmRegisteredToken";

export async function ensureServiceWorkerRegistered(): Promise<boolean> {
  try {
    if (!("serviceWorker" in navigator)) {
      console.warn("FCM: Service Worker not supported in this browser.");
      return false;
    }

    const existingReg = await navigator.serviceWorker.getRegistration("/firebase-messaging-sw.js");
    if (existingReg) {
      console.log("FCM: Service worker already registered:", existingReg.scope);
      return true;
    }

    console.log("FCM: Registering firebase-messaging-sw.js...");
    const reg = await navigator.serviceWorker.register("/firebase-messaging-sw.js");
    console.log("FCM: Service worker registered:", reg.scope);
    return true;
  } catch (error) {
    console.error("FCM: Service worker registration failed:", error);
    return false;
  }
}

export async function requestNotificationPermission() {
  try {
    if (!("Notification" in window)) {
      console.warn("FCM: This browser does not support notifications.");
      return null;
    }

    console.log("FCM: Current notification permission:", Notification.permission);

    if (Notification.permission === "denied") {
      console.warn("FCM: Notification permission previously denied. Cannot request again.");
      return null;
    }

    if (Notification.permission !== "granted") {
      const permission = await Notification.requestPermission();
      console.log("FCM: Permission request result:", permission);

      if (permission !== "granted") {
        console.warn("FCM: Notification permission not granted.");
        return null;
      }
    }

    console.log("FCM: Requesting token with VAPID key...");

    const swReady = await ensureServiceWorkerRegistered();
    if (!swReady) {
      console.warn("FCM: Service worker not available, cannot get token.");
      return null;
    }

    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
    });

    if (!token) {
      console.warn("FCM: getToken returned empty/null token.");
      return null;
    }

    console.log("FCM: Token retrieved successfully:", token.substring(0, 20) + "...");

    return token;
  } catch (error) {
    console.error("FCM: Error getting FCM token:", error);
    return null;
  }
}

export async function registerDeviceToken(fcmToken: string): Promise<boolean> {
  try {
    const response = await authenticatedFetch(REGISTER_DEVICE_TOKEN_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json", accept: "application/json" },
      body: JSON.stringify({ token: fcmToken }),
    });

    if (response.ok) {
      localStorage.setItem(FCM_REGISTERED_TOKEN_KEY, fcmToken);
      console.log("FCM: Token registered successfully.");
      return true;
    }

    console.error("FCM: Failed to register device token:", response.status, response.statusText);
    return false;
  } catch (error) {
    console.error("Error registering device token:", error);
    return false;
  }
}

export async function registerDeviceTokenIfChanged(): Promise<void> {
  try {
    if (!("Notification" in window)) {
      console.warn("FCM (registerDeviceTokenIfChanged): Browser does not support notifications.");
      return;
    }

    if (Notification.permission !== "granted") {
      console.log("FCM (registerDeviceTokenIfChanged): Permission not granted, attempting request...");
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        console.warn("FCM (registerDeviceTokenIfChanged): Permission not granted after request.");
        return;
      }
    }

    console.log("FCM (registerDeviceTokenIfChanged): Getting token...");

    const swReady = await ensureServiceWorkerRegistered();
    if (!swReady) {
      console.warn("FCM (registerDeviceTokenIfChanged): Service worker not available, cannot get token.");
      return;
    }

    const currentToken = await getToken(messaging, { vapidKey: VAPID_KEY });
    if (!currentToken) {
      console.warn("FCM (registerDeviceTokenIfChanged): getToken returned empty token.");
      return;
    }

    const lastRegistered = localStorage.getItem(FCM_REGISTERED_TOKEN_KEY);
    if (currentToken === lastRegistered) {
      console.log("FCM (registerDeviceTokenIfChanged): Token unchanged, skipping registration.");
      return;
    }

    console.log("FCM (registerDeviceTokenIfChanged): Token changed, registering new token...");
    await registerDeviceToken(currentToken);
  } catch (error) {
    console.error("FCM (registerDeviceTokenIfChanged): Error:", error);
  }
}

export function clearRegisteredDeviceToken(): void {
  localStorage.removeItem(FCM_REGISTERED_TOKEN_KEY);
}